import CloseIcon from '@mui/icons-material/Close';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, TextField } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { UserContext } from '../../../context/UserContext';
import AttToken from '../../../helpers/attToken';
import { trackEventMatomo } from '../../../helpers/matomo';
import Places from '../../../service/Places';
import { PlaceType } from '../../../types/places/types';
import queryClient from '../../../service/query';
import PlacesAutocomplete from './PalcesAutoComplete';
import { PlaceToSend } from '../../../types/places/types';

const PlaceService = new Places();

export type selectedPlaceType = {
  lat: number, lng: number, address: string
};

export default function NewPlaceModal({ open, setOpen, customerId, local }: { open: boolean, setOpen: Function, customerId: string, local?: PlaceType }) {
  const { t } = useTranslation('translation');
  const { hierarchyLabel } = useContext(UserContext);
  const [place, setPlace] = useState<PlaceType | null>(local ?? null);

  const { mutate: create, isPending: isPendingCreate } = useMutation({
    mutationKey: ['CREATE_NEW_PLACE'],
    mutationFn: async () => {
      const token = await AttToken();

      if (!token) {
        throw new Error('Token not found');
      }

      const PlaceObj: PlaceToSend = {
        userOwner: 'Administrador Axyma',
        cliente: customerId,
        conjuntos: [],
        dataAlteracao: Date.now(),
        lat: place?.lat ?? 0,
        lng: place?.lng ?? 0,
        localizacao: place?.localizacao ?? '',
        nome: place?.nome ?? '',
      };

      return toast.promise(
        PlaceService.newPlace(token, PlaceObj),
        {
          pending: `Salvando ${hierarchyLabel.local}...`,
          success: `${hierarchyLabel.local} ${t('created')}!`,
          error: `Erro ao salvar ${hierarchyLabel.local}!`
        },
        {
          toastId: `create-place-${PlaceObj.nome}`
        }
      ).then((newPlace) => {
        if (!newPlace) {
          throw new Error('Error creating new place');
        }
        return newPlace;
      });
    },
    onSuccess: () => {
      setOpen(false);
      queryClient.refetchQueries({ queryKey: ['GET_PLACES'] });
    }
  });

  const { mutate: edit, isPending: isPendingEdit } = useMutation({
    mutationKey: ['EDIT_PLACE'],
    mutationFn: async () => {
      const token = await AttToken();

      if (!token) {
        throw new Error('Token not found');
      }

      const payload = {
        lat: place?.lat ?? 0,
        lng: place?.lng ?? 0,
        localizacao: place?.localizacao ?? '',
        nome: place?.nome ?? '',
        id: place?.id ?? ''
      };

      return toast.promise(
        PlaceService.editPlace(token, payload),
        {
          pending: `Editando ${hierarchyLabel.local}...`,
          success: `${hierarchyLabel.local} ${t('edited')}!`,
          error: `Erro ao editar ${hierarchyLabel.local}!`
        },
        {
          toastId: `edit-place-${payload.id}`
        }
      ).then((response) => {
        if (!response) {
          throw new Error('Error editing place');
        }
        return response;
      });
    },
    onSuccess: () => {
      setOpen(false);
      queryClient.refetchQueries({ queryKey: ['GET_PLACES'] });
    }
  });

  const onLoading = isPendingCreate || isPendingEdit;

  const submit = () => {
    trackEventMatomo('Modal criação de local', 'click', 'button', 'salvar');
    place?.id ? edit() : create();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        scroll={'paper'}
        fullWidth
        maxWidth='sm'
      >
        <DialogTitle>{
          place?.id
            ? `${t('Services.ServiceModal.editLocation')}`
            : `${t('Services.ServiceModal.addLocation')}`}</DialogTitle>
        <IconButton
          aria-label="close"
          onClick={() => setOpen(false)}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers={true}>
          <Stack width='100%' spacing={2} justifyContent='flex-start' alignItems='flex-start'>
            <TextField
              onClick={() => trackEventMatomo('Modal criação de local', 'click', 'textfield', 'edita valor')}
              label={t('name')}
              fullWidth
              value={place?.nome || ''}
              onChange={(e) => setPlace((prev: PlaceType | null) => prev
                ? { ...prev, nome: e.target.value }
                : { nome: e.target.value })}
            />
            <PlacesAutocomplete place={place} setPlace={setPlace} />
            {(!place?.lng || !place?.lat) && (
              <span className='tag is-warning'>{t('Services.ServiceModal.pleaseSelectLocationFromList')}</span>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={submit}
            disabled={!place?.nome?.length
              || !place?.localizacao?.length
              || onLoading}
          >{t('save')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
